// Backend error codes and their user-friendly messages
export const ERROR_MESSAGES: Record<string, string> = {
  // Reference processing errors
  REFERENCE_NOT_FOUND_ERROR:
    "The uploaded file could not be found in your book. Please try uploading the file again.",
  EVENT_DOCUMENT_MISMATCH_ERROR:
    "The uploaded file doesn't match this document's expected type. Please try uploading the file again.",
  TOKEN_LIMIT_EXCEEDED:
    "This file is too large to process. Please try uploading a smaller file or break it into multiple documents.",
  PROCESSING_ERROR:
    "There was an error processing your file. Please try uploading it again.",
  RETRY_ATTEMPTS_EXHAUSTED:
    "Processing failed after several attempts. Please try again.",
  UPLOAD_SUPERSEDED:
    "This upload was replaced by a newer version of the file before processing finished.",
  METADATA_EXTRACTION_ERROR:
    "There was an error extracting metadata from the file. The file may be corrupted or contains no text.",

  // Book errors
  BOOK_NOT_FOUND_ERROR:
    "The book you're trying to access could not be found. Please return to the home page and select a different book.",

  // Author style extraction errors
  AUTHOR_STYLE_EXTRACTION_ERROR:
    "Author style extraction failed. Please ensure you have uploaded style reference documents and try again.",

  // Chapter and content errors
  CHAPTER_NOT_FOUND_ERROR:
    "The requested chapter could not be found. Navigate back to the table of contents to select another chapter.",
  HEADING_NOT_FOUND_ERROR:
    "The requested section could not be found. Try refreshing the page or go back to the chapter view.",
  PARAGRAPH_NOT_FOUND_ERROR:
    "The requested paragraph could not be found. Try refreshing the page or go back to the chapter view.",

  // Generation errors
  GENERATION_ERROR:
    "There was an error generating content. Please try again or contact support if the problem persists.",
  HEADING_STRUCTURE_MISMATCH_ERROR:
    "The document structure doesn't match the expected format. Please check your content and try again.",
  UNSUPPORTED_GENERATION_TYPE:
    "This type of content generation is not currently supported.",
  CHAPTER_PLAN_ITERATION_LIMIT_ERROR:
    "Planning spent too long searching your reference documents and stopped before writing a plan. Please try again, or narrow the chapter thesis to give it a tighter target.",
  EDITING_BUDGET_EXCEEDED_ERROR:
    "The refinement needed more edits or time than one run allows, so the chapter was left unchanged. Please try again with a narrower request, or split it into smaller ones.",
  EDITING_STOPPED_ERROR:
    "The refinement stopped before it finished, so the chapter was left unchanged. Please try again, or rephrase the request.",
  CHAPTER_PLAN_GENERATION_IN_PROGRESS_ERROR:
    "A plan is already being generated for this chapter. Please wait for it to finish.",
  CHAPTER_CONTENT_GENERATION_IN_PROGRESS_ERROR:
    "Content is already being generated for this chapter. Please wait for it to finish.",
  TOC_GENERATION_IN_PROGRESS_ERROR:
    "A table of contents is already being generated for this book. Please wait for it to finish.",
  DOCX_EXPORT_IN_PROGRESS_ERROR:
    "An export is already in progress for this book. Please wait for it to finish.",

  CHAPTER_CONTENT_NOT_GENERATED_ERROR:
    "This chapter has no content yet. Generate content before attempting refinement.",

  CHAPTER_PLAN_NOT_GENERATED_ERROR:
    "This chapter has no plan yet. Generate a plan before attempting refinement.",

  // Chapter review errors
  CHAPTER_REVIEW_IN_PROGRESS_ERROR:
    "This chapter is already being reviewed. Please wait for it to finish.",
  CHAPTER_ATTRIBUTION_NOT_READY_ERROR:
    "This chapter's sources are not linked yet, so there is nothing to review against. Wait for processing to finish, then run the review again.",
  FAITHFULNESS_CLAIMS_INCOMPLETE_ERROR:
    "The review could not check every sentence, so its findings would be incomplete. Please run the review again.",
  CHAPTER_REVIEW_DRAFT_REPLACED_ERROR:
    "This review was replaced by a newer one before it finished. Its findings were discarded.",
  FACTUALITY_REVIEW_BUDGET_EXCEEDED_ERROR:
    "The review ran out of time before it finished checking this chapter's claims. Please retry.",
  FACTUALITY_WEB_SEARCH_UNAVAILABLE_ERROR:
    "The review could not search for external sources to check this chapter's claims. Please retry in a few minutes.",
  FACTUALITY_REVIEW_TURN_UNFINISHED_ERROR:
    "The review stopped before it finished checking this chapter's claims, so its findings would be incomplete. Please retry.",
  FACTUALITY_REVIEW_REFUSED_ERROR:
    "The review declined to check this chapter's claims, so its findings would be incomplete.",

  // Workflow errors
  CHAPTER_NOT_PLANNED_ERROR:
    "This chapter has not been planned yet. Please complete the chapter plan first.",
  PREVIOUS_CHAPTER_NOT_PLANNED_ERROR:
    "Please complete the plan for previous chapters before proceeding.",
  PREVIOUS_CHAPTER_SUMMARY_NOT_READY_ERROR:
    "An earlier chapter is still being processed. Please wait a moment and try again. If this keeps happening, regenerate that chapter's content.",
  PREVIOUS_CHAPTER_SUMMARY_FAILED_ERROR:
    "An earlier chapter could not finish processing. Regenerate that chapter's content, then try again.",
  PREVIOUS_HEADING_NOT_OUTLINED_ERROR:
    "Please complete the outline for previous sections before proceeding.",
  PREVIOUS_CHAPTER_NOT_OUTLINED_ERROR:
    "Please complete the outline for previous chapters before proceeding.",
  PREVIOUS_BULLET_POINT_NOT_WRITTEN_ERROR:
    "Please complete the previous bullet points before proceeding.",
  PREVIOUS_HEADING_NOT_WRITTEN_ERROR:
    "Please complete the previous sections before proceeding.",

  // Full book generation errors
  FULL_BOOK_GENERATION_IN_PROGRESS_ERROR:
    "The full book is already being generated. Please wait for it to finish.",
  FULL_BOOK_GENERATION_ABANDONED_ERROR:
    "This full book generation stopped responding and was replaced by a newer request.",
  FULL_BOOK_GENERATION_SUPERSEDED_ERROR:
    "This full book generation was replaced by a newer request.",
  BOOK_STRUCTURE_ERROR:
    "The book's chapters could not be read. Refresh and try again.",
  FULL_BOOK_GENERATION_ERROR:
    "Full book generation stopped unexpectedly. Generate the full book again to resume from the last completed chapter, or contact support if the problem persists.",
  CHILD_GENERATION_REQUEST_FAILED_ERROR:
    "A chapter's plan or content could not be generated. Open that chapter and generate it again, then generate the full book to resume from the last completed chapter.",
  CHILD_GENERATION_REQUEST_NOT_FOUND_ERROR:
    "A chapter's plan or content could not be found during full book generation. Generate the full book again to resume from the last completed chapter.",
  CHILD_GENERATION_REQUEST_TIMEOUT_ERROR:
    "A chapter's plan or content took too long to generate. Generate the full book again to resume from the last completed chapter.",
  CHAPTER_COMPLETION_TIMEOUT_ERROR:
    "A chapter took too long to finish during full book generation. Generate the full book again to resume from the last completed chapter.",
  CHAPTER_SUMMARY_FAILED_ERROR:
    "A chapter could not be processed, so the next chapter could not start. Regenerate that chapter's content, then generate the full book again.",
  CHAPTER_SUMMARY_TIMEOUT_ERROR:
    "Processing a chapter took too long, so the next chapter could not start. Generate the full book again to resume. If this keeps happening, regenerate that chapter's content first.",

  // Infrastructure errors
  INVALID_REQUEST_ERROR:
    "The request is invalid. Please check your input and try again.",
  IDEMPOTENCY_ERROR:
    "This operation is already in progress. Please wait for it to complete.",
  AGENT_TOOL_FATAL_ERROR:
    "Searching your reference documents failed repeatedly during generation. Please try again.",
  CHUNK_SEARCH_ERROR:
    "There was a problem searching your reference documents. Please try again.",

  // Authentication errors
  UNAUTHORIZED_ERROR:
    "You don't have permission to perform this action. Please sign in and try again.",

  // Client-side validation errors
  INVALID_DOCUMENT_ERROR:
    "Some of this book's data could not be read. Please refresh the page, or contact support if the problem persists.",

  // Generic fallback
  UNKNOWN_ERROR:
    "An unexpected error occurred. Please try again, refresh the page, or contact support if the problem persists.",
};

/**
 * Get a user-friendly error message for a backend error code
 */
export const getErrorMessage = (
  errorCode: string | null | undefined,
): string => {
  if (!errorCode) {
    return ERROR_MESSAGES.UNKNOWN_ERROR;
  }

  return ERROR_MESSAGES[errorCode] || ERROR_MESSAGES.UNKNOWN_ERROR;
};
